<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { compositeToCategory } from '@/features/scoring/lib/scoringHelpers'
import type { UFPMMember } from '../lib/types'

type ChartMode = 'donut' | 'bar'
type CategoryKey = 'excellent' | 'satisfactory' | 'unsatisfactory' | 'exempt'

const props = defineProps<{
  members: readonly UFPMMember[]
}>()

const chartMode = ref<ChartMode>('donut')
const yearFilter = ref('')
const categoryFilter = ref('')
const statusFilter = ref('')
const squadronFilter = ref('')
const canvasRef = ref<HTMLCanvasElement | null>(null)
let ro: ResizeObserver | null = null

const CATEGORY_META: Record<CategoryKey, { label: string; color: string }> = {
  excellent: { label: 'Excellent', color: '#1f5f35' },
  satisfactory: { label: 'Satisfactory', color: '#c6a01b' },
  unsatisfactory: { label: 'Unsatisfactory', color: '#c91f2d' },
  exempt: { label: 'Composite Exempt', color: '#2323b8' },
}

function clean(value: unknown): string {
  return String(value ?? '').trim()
}

function parseDate(value: unknown): Date | null {
  const raw = clean(value)
  if (!raw) return null
  const direct = new Date(raw)
  if (!Number.isNaN(direct.getTime())) return direct
  const match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/)
  if (!match) return null
  const month = Number(match[1])
  const day = Number(match[2])
  const year = Number(match[3]?.length === 2 ? `20${match[3]}` : match[3])
  const parsed = new Date(year, month - 1, day)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function latestTestDate(member: UFPMMember): Date | null {
  return parseDate(
    member.history?.[0]?.recordedDate
    || member.history?.[0]?.date
    || member.latestFitnessAssessmentCompletion
    || member.lastTestDate
  )
}

function displaySquadron(value: unknown): string {
  return clean(value)
    .replace(/^\d{4}\s+/, '')
    .replace(/\s+\d{4}$/, '')
    .replace(/\s+/g, ' ')
    || 'Unknown'
}

function memberCategoryLabel(member: UFPMMember): string {
  const entry = member.history?.[0]
  const fromHistory = clean(entry?.cat)
  if (fromHistory) return fromHistory
  const composite = Number(entry?.composite ?? member.compositeScore)
  if (Number.isFinite(composite) && composite > 0) return compositeToCategory(composite)
  return clean(member.latestFitnessAssessmentCategory)
}

function categoryKey(member: UFPMMember): CategoryKey {
  const raw = memberCategoryLabel(member).toLowerCase()
  if (raw.includes('excellent') || raw.startsWith('exc')) return 'excellent'
  if (raw.includes('unsat') || raw.includes('poor') || raw.includes('fail')) return 'unsatisfactory'
  if (raw.includes('satisfactory') || raw.startsWith('sat')) return 'satisfactory'
  return 'exempt'
}

function isReady(member: UFPMMember): boolean {
  return clean(member.fitnessCurrency).toLowerCase() === 'ready'
}

const years = computed(() => {
  const values = new Set<string>()
  for (const member of props.members) {
    const date = latestTestDate(member)
    if (date) values.add(String(date.getFullYear()))
  }
  return [...values].sort((a, b) => Number(b) - Number(a))
})

const categories = computed(() =>
  [...new Set(
    props.members
      .map(member => memberCategoryLabel(member))
      .filter(Boolean),
  )].sort()
)

const statuses = computed(() =>
  [...new Set(props.members.map(member => clean(member.fitnessCurrency)).filter(Boolean))].sort()
)

const squadrons = computed(() =>
  [...new Set(props.members.map(member => clean(member.organization)).filter(Boolean))]
    .sort((a, b) => displaySquadron(a).localeCompare(displaySquadron(b)))
)

const filteredMembers = computed(() =>
  props.members.filter(member => {
    const date = latestTestDate(member)
    const year = date ? String(date.getFullYear()) : ''
    return (!yearFilter.value || year === yearFilter.value)
      && (!categoryFilter.value || memberCategoryLabel(member) === categoryFilter.value)
      && (!statusFilter.value || clean(member.fitnessCurrency) === statusFilter.value)
      && (!squadronFilter.value || clean(member.organization) === squadronFilter.value)
  })
)

const categoryCounts = computed(() => {
  const counts: Record<CategoryKey, number> = { excellent: 0, satisfactory: 0, unsatisfactory: 0, exempt: 0 }
  for (const member of filteredMembers.value) counts[categoryKey(member)] += 1
  return counts
})

const chartRows = computed(() =>
  (Object.keys(CATEGORY_META) as CategoryKey[])
    .map(key => ({ key, ...CATEGORY_META[key], count: categoryCounts.value[key] }))
    .filter(row => row.count > 0)
)

const metrics = computed(() => {
  const total = filteredMembers.value.length
  const ready = filteredMembers.value.filter(isReady).length
  return {
    excellentPct: pct(categoryCounts.value.excellent, total),
    satisfactoryPct: pct(categoryCounts.value.satisfactory, total),
    unsatisfactoryPct: pct(categoryCounts.value.unsatisfactory, total),
    exemptPct: pct(categoryCounts.value.exempt, total),
    notReadyPct: pct(Math.max(0, total - ready), total),
  }
})

function pct(count: number, total: number): string {
  return total ? ((count / total) * 100).toFixed(1) : '0.0'
}

function clearFilters(): void {
  yearFilter.value = ''
  categoryFilter.value = ''
  statusFilter.value = ''
  squadronFilter.value = ''
}

function drawChart(): void {
  const canvas = canvasRef.value
  const wrap = canvas?.parentElement
  const ctx = canvas?.getContext('2d')
  if (!canvas || !wrap || !ctx) return

  const cssW = Math.max(1, Math.floor(wrap.clientWidth || 320))
  const cssH = Math.max(1, Math.floor(wrap.clientHeight || 280))
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  canvas.width = Math.floor(cssW * dpr)
  canvas.height = Math.floor(cssH * dpr)
  canvas.style.width = cssW + 'px'
  canvas.style.height = cssH + 'px'
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, cssW, cssH)

  if (!filteredMembers.value.length) {
    ctx.fillStyle = getCss('--muted', '#6f8192')
    ctx.font = '12px Barlow'
    ctx.textAlign = 'center'
    ctx.fillText('No roster data matches the current filters', cssW / 2, cssH / 2)
    return
  }

  chartMode.value === 'donut' ? drawDonut(ctx, cssW, cssH) : drawBars(ctx, cssW, cssH)
}

function getCss(name: string, fallback: string): string {
  return getComputedStyle(document.body).getPropertyValue(name).trim() || fallback
}

function drawDonut(ctx: CanvasRenderingContext2D, width: number, _height: number): void {
  const total = filteredMembers.value.length
  const cx = width / 2
  const cy = 126
  const radius = Math.min(88, width * 0.28)
  const inner = radius * 0.58
  let angle = -Math.PI / 2
  for (const row of chartRows.value) {
    const slice = (row.count / total) * Math.PI * 2
    ctx.beginPath()
    ctx.moveTo(cx, cy)
    ctx.arc(cx, cy, radius, angle, angle + slice)
    ctx.closePath()
    ctx.fillStyle = row.color
    ctx.fill()
    angle += slice
  }
  ctx.globalCompositeOperation = 'destination-out'
  ctx.beginPath()
  ctx.arc(cx, cy, inner, 0, Math.PI * 2)
  ctx.fill()
  ctx.globalCompositeOperation = 'source-over'

  drawLegend(ctx, width, 226)
}

function drawBars(ctx: CanvasRenderingContext2D, width: number, _height: number): void {
  const max = Math.max(1, ...chartRows.value.map(row => row.count))
  const left = 118
  const right = 48
  const top = 36
  const barH = 28
  const gap = 18
  const chartW = width - left - right
  ctx.font = '11px Barlow Condensed'
  ctx.textAlign = 'right'
  ctx.textBaseline = 'middle'
  for (const [idx, row] of chartRows.value.entries()) {
    const y = top + idx * (barH + gap)
    const w = (row.count / max) * chartW
    ctx.fillStyle = getCss('--muted', '#6f8192')
    ctx.fillText(row.label.toUpperCase(), left - 8, y + barH / 2)
    ctx.fillStyle = row.color
    ctx.fillRect(left, y, Math.max(3, w), barH)
    ctx.fillStyle = getCss('--fg', '#f5f7fa')
    ctx.textAlign = 'left'
    ctx.fillText(String(row.count), left + Math.max(6, w) + 8, y + barH / 2)
    ctx.textAlign = 'right'
  }
  drawLegend(ctx, width, 226)
}

function drawLegend(ctx: CanvasRenderingContext2D, width: number, y: number): void {
  const rows = (Object.keys(CATEGORY_META) as CategoryKey[]).map(key => ({ key, ...CATEGORY_META[key] }))
  const itemW = Math.max(110, width / 4)
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.font = '10px Barlow'
  rows.forEach((row, idx) => {
    const x = 10 + (idx % 2) * itemW
    const yy = y + Math.floor(idx / 2) * 18
    ctx.fillStyle = row.color
    ctx.beginPath()
    ctx.arc(x + 5, yy, 4, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = getCss('--muted', '#6f8192')
    ctx.fillText(row.label, x + 14, yy)
  })
}

watch([() => props.members, filteredMembers, chartMode], () => nextTick(drawChart), { deep: true })
onMounted(() => {
  nextTick(drawChart)
  if (canvasRef.value?.parentElement) {
    ro = new ResizeObserver(drawChart)
    ro.observe(canvasRef.value.parentElement)
  }
})
onUnmounted(() => ro?.disconnect())
</script>

<template>
  <section class="ufpm-dashboard" aria-label="UFPM readiness dashboard">
    <div class="ufpm-dashboard-head">
      <div>
        <div class="acc-title" style="margin:0;">UFPM Dashboard</div>
        <h2>Fitness Readiness</h2>
      </div>
      <div class="ufpm-chart-toggle" role="group" aria-label="Chart view">
        <button type="button" :class="{ active: chartMode === 'donut' }" @click="chartMode = 'donut'">Donut</button>
        <button type="button" :class="{ active: chartMode === 'bar' }" @click="chartMode = 'bar'">Bar</button>
      </div>
    </div>

    <div class="ufpm-dashboard-grid">
      <div class="ufpm-chart-card">
        <div class="ufpm-chart-title">
          {{ chartMode === 'donut' ? 'Fitness Composition by Category (%)' : 'Fitness Composition by Category (Count)' }}
        </div>
        <div class="ufpm-chart-wrap">
          <canvas ref="canvasRef" aria-label="Fitness category chart"></canvas>
        </div>
      </div>

      <div class="ufpm-kpi-stack">
        <div class="ufpm-kpi-row excellent"><strong>{{ metrics.excellentPct }}%</strong><span>Excellent</span></div>
        <div class="ufpm-kpi-row satisfactory"><strong>{{ metrics.satisfactoryPct }}%</strong><span>Satisfactory</span></div>
        <div class="ufpm-kpi-row unsatisfactory"><strong>{{ metrics.unsatisfactoryPct }}%</strong><span>Unsatisfactory</span></div>
        <div class="ufpm-kpi-row exempt"><strong>{{ metrics.exemptPct }}%</strong><span>Composite Exempt</span></div>
        <div class="ufpm-kpi-row"><strong>{{ metrics.notReadyPct }}%</strong><span>Not Ready</span></div>
      </div>
    </div>

    <div class="ufpm-dashboard-filters">
      <div class="field">
        <label for="ufpm-dash-year">Year</label>
        <select id="ufpm-dash-year" v-model="yearFilter">
          <option value="">All years</option>
          <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
        </select>
      </div>
      <div class="field">
        <label for="ufpm-dash-category">Fitness Category</label>
        <select id="ufpm-dash-category" v-model="categoryFilter">
          <option value="">All categories</option>
          <option v-for="category in categories" :key="category" :value="category">{{ category }}</option>
        </select>
      </div>
      <div class="field">
        <label for="ufpm-dash-status">Fitness Status</label>
        <select id="ufpm-dash-status" v-model="statusFilter">
          <option value="">All statuses</option>
          <option v-for="status in statuses" :key="status" :value="status">{{ status }}</option>
        </select>
      </div>
      <div class="field">
        <label for="ufpm-dash-squadron">Squadron</label>
        <select id="ufpm-dash-squadron" v-model="squadronFilter">
          <option value="">All squadrons</option>
          <option v-for="squadron in squadrons" :key="squadron" :value="squadron">
            {{ displaySquadron(squadron) }}
          </option>
        </select>
      </div>
      <button class="btn btn-secondary ufpm-dashboard-clear" type="button" @click="clearFilters">Clear</button>
    </div>
  </section>
</template>
